import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabase";

import WelcomeChoice from "./steps/WelcomeChoice";
import HowToServe from "./steps/HowToServe";
import SkillsNeeded from "./steps/SkillsNeeded";
import BasicInfo from "./steps/BasicInfo";
import ChurchAffiliation from "./steps/ChurchAffiliation";
import Reference from "./steps/Reference";
import OnboardingComplete from "./steps/OnboardingComplete";

const STORAGE_KEY = "collavo_onboarding_v2";

// Step sequences per role
const STEPS = {
  serve:  ["howToServe",  "basicInfo", "churchAffiliation", "reference", "complete"],
  invite: ["skillsNeeded","basicInfo", "churchAffiliation", "reference", "complete"],
  both:   ["howToServe",  "skillsNeeded", "basicInfo", "churchAffiliation", "reference", "complete"],
};

function getSteps(role) {
  return STEPS[role] || [];
}

const defaultFormData = {
  name: "", location: "", profilePictureUrl: "", skillTitle: "",
  waysToHelp: [], skillsNeeded: [],
  churchAffiliation: "affiliated", churchName: "", churchCity: "", denomination: "", churchRole: "",
  refereeName: "", refereeEmail: "", refereePhone: "", refereeRole: "",
};

export default function OnboardingFlow() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [userRole, setUserRole] = useState(null); // null = not chosen yet
  const [currentStep, setCurrentStep] = useState(null); // null = welcome screen
  const [submitting, setSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...defaultFormData, ...JSON.parse(saved) } : { ...defaultFormData };
    } catch {
      return { ...defaultFormData };
    }
  });

  // Get authenticated user and check if already complete
  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/"); return; }
      setUserId(user.id);

      const { data: profile } = await supabase
        .from("user_profiles")
        .select("profile_complete, user_role, full_name, location, profile_picture_url, bio1, ways_to_help, skills_needed, church_name, church_city, denomination, church_role, referee_name, referee_email, referee_phone, referee_role")
        .eq("user_id", user.id)
        .single();

      if (profile) {
        // Pre-fill from any saved Supabase data
        const merged = {
          name: profile.full_name || formData.name,
          location: profile.location || formData.location,
          profilePictureUrl: profile.profile_picture_url || formData.profilePictureUrl,
          skillTitle: profile.bio1 || formData.skillTitle,
          waysToHelp: profile.ways_to_help || formData.waysToHelp,
          skillsNeeded: profile.skills_needed || formData.skillsNeeded,
          churchAffiliation: profile.church_name ? "affiliated" : (formData.churchAffiliation || "affiliated"),
          churchName: profile.church_name || formData.churchName,
          churchCity: profile.church_city || formData.churchCity,
          denomination: profile.denomination || formData.denomination,
          churchRole: profile.church_role || formData.churchRole,
          refereeName: profile.referee_name || formData.refereeName,
          refereeEmail: profile.referee_email || formData.refereeEmail,
          refereePhone: profile.referee_phone || formData.refereePhone,
          refereeRole: profile.referee_role || formData.refereeRole,
        };
        setFormData(merged);
        if (profile.profile_picture_url) setImagePreview(profile.profile_picture_url);

        // Restore role from Supabase if partially through onboarding
        if (profile.user_role) setUserRole(profile.user_role);
      }
    };
    init();
  }, []);

  // Persist formData to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { alert("Please select an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { alert("Image size must be less than 5MB"); return; }

    try {
      setUploadingImage(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const fileExt = file.name.split(".").pop();
      const filePath = `profile-pictures/${user.id}-${Date.now()}.${fileExt}`;
      const { error } = await supabase.storage.from("profile-pictures").upload(filePath, file, { cacheControl: "3600", upsert: false });
      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage.from("profile-pictures").getPublicUrl(filePath);
      handleInputChange("profilePictureUrl", publicUrl);
      setImagePreview(publicUrl);
    } catch (error) {
      console.error("Image upload error:", error);
      alert("Failed to upload image. Please try again.");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleChoose = async (role) => {
    setUserRole(role);
    setCurrentStep(0);
    // Save role choice to Supabase immediately
    if (userId) {
      await supabase.from("user_profiles").upsert({
        user_id: userId,
        user_role: role,
        skill_provider: role === "serve" || role === "both",
        skill_requester: role === "invite" || role === "both",
        updated_at: new Date().toISOString(),
      }, { onConflict: "user_id" });
    }
  };

  const handleContinue = async () => {
    const steps = getSteps(userRole);
    const nextStep = currentStep + 1;

    // Auto-save progress to Supabase on each step
    if (userId) {
      await supabase.from("user_profiles").upsert({
        user_id: userId,
        full_name: formData.name || null,
        location: formData.location || null,
        profile_picture_url: formData.profilePictureUrl || null,
        bio1: formData.skillTitle || null,
        ways_to_help: formData.waysToHelp || [],
        skills_needed: formData.skillsNeeded || [],
        church_name: formData.churchAffiliation === "affiliated" ? formData.churchName : null,
        church_city: formData.churchAffiliation === "affiliated" ? formData.churchCity : null,
        denomination: formData.churchAffiliation === "affiliated" ? formData.denomination : null,
        church_role: formData.churchAffiliation === "affiliated" ? formData.churchRole : null,
        referee_name: formData.refereeName || null,
        referee_email: formData.refereeEmail || null,
        referee_phone: formData.refereePhone || null,
        referee_role: formData.refereeRole || null,
        updated_at: new Date().toISOString(),
      }, { onConflict: "user_id" });
    }

    // Last real step before complete — submit and mark profile complete
    if (steps[nextStep] === "complete") {
      await handleFinalSubmit();
    } else {
      setCurrentStep(nextStep);
    }
  };

  const handleFinalSubmit = async () => {
    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const referenceToken = crypto.randomUUID();
      const applicantName = formData.name || "A Collavo applicant";

      await supabase.from("user_profiles").upsert({
        user_id: user.id,
        full_name: formData.name || null,
        location: formData.location || null,
        profile_picture_url: formData.profilePictureUrl || null,
        bio1: formData.skillTitle || null,
        ways_to_help: formData.waysToHelp || [],
        skills_needed: formData.skillsNeeded || [],
        church_name: formData.churchAffiliation === "affiliated" ? formData.churchName : null,
        church_city: formData.churchAffiliation === "affiliated" ? formData.churchCity : null,
        denomination: formData.churchAffiliation === "affiliated" ? formData.denomination : null,
        church_role: formData.churchAffiliation === "affiliated" ? formData.churchRole : null,
        referee_name: formData.refereeName || null,
        referee_email: formData.refereeEmail || null,
        referee_phone: formData.refereePhone || null,
        referee_role: formData.refereeRole || null,
        skill_provider: userRole === "serve" || userRole === "both",
        skill_requester: userRole === "invite" || userRole === "both",
        user_role: userRole,
        profile_complete: true,
        profile_visible: false,
        application_submitted: true,
        reference_status: "pending",
        reference_token: referenceToken,
        updated_at: new Date().toISOString(),
      }, { onConflict: "user_id" });

      // Send reference verification email
      const verificationLink = `${window.location.origin}/verify-reference/${referenceToken}`;
      try {
        await supabase.functions.invoke("send-email", {
          body: {
            to: formData.refereeEmail,
            subject: `Church Reference Request for ${applicantName}`,
            html: `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
body{font-family:Arial,sans-serif;line-height:1.6;color:#333;max-width:600px;margin:0 auto;padding:20px;background:#f9f9f9}
.container{background:#fff;border-radius:12px;padding:32px;box-shadow:0 2px 8px rgba(0,0,0,.08)}
h1{color:#1a1a1a;font-size:24px;margin:0 0 8px}
.info-box{background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:20px;margin:24px 0}
.cta{display:inline-block;background:#1a1a1a;color:#fff!important;text-decoration:none;padding:14px 32px;border-radius:8px;font-weight:600;font-size:16px;margin:24px 0}
.footer{margin-top:32px;padding-top:20px;border-top:1px solid #e5e5e5;text-align:center;font-size:13px;color:#9ca3af}
</style></head><body><div class="container">
<h1>Church Reference Request</h1><p>from Collavo</p>
<p>Hi ${formData.refereeName},</p>
<p><strong>${applicantName}</strong> has applied to Collavo and has listed you as their church reference.</p>
<div class="info-box">
  <p><strong>Applicant:</strong> ${applicantName}</p>
  ${formData.churchName ? `<p><strong>Church:</strong> ${formData.churchName}</p>` : ""}
  ${formData.location ? `<p><strong>Location:</strong> ${formData.location}</p>` : ""}
</div>
<p>By confirming this reference, you verify that you know this person and that they are part of your church community.</p>
<div style="text-align:center"><a href="${verificationLink}" class="cta">Confirm Reference</a></div>
<p style="font-size:13px;color:#737373">If you don't recognise this person, you can safely ignore this email.</p>
<div class="footer"><p>Automated message from Collavo. Please do not reply.</p></div>
</div></body></html>`,
          },
        });
      } catch (emailError) {
        console.error("Reference email error:", emailError);
        // Don't block completion if email fails
      }

      // Clear localStorage and advance to complete screen
      localStorage.removeItem(STORAGE_KEY);
      setCurrentStep(getSteps(userRole).indexOf("complete"));
    } catch (error) {
      console.error("Submission error:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (currentStep === 0) {
      // Go back to welcome choice
      setCurrentStep(null);
      setUserRole(null);
    } else {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // --- Render ---

  // Welcome screen (no role chosen yet)
  if (userRole === null || currentStep === null) {
    return <WelcomeChoice onChoose={handleChoose} />;
  }

  const steps = getSteps(userRole);
  const stepName = steps[currentStep];
  // For progress bar: exclude 'complete' from total count
  const formSteps = steps.filter((s) => s !== "complete");
  const stepNumber = currentStep + 1;
  const totalSteps = formSteps.length;

  const sharedProps = { formData, onInputChange: handleInputChange, onContinue: handleContinue, onBack: handleBack, stepNumber, totalSteps };

  if (stepName === "howToServe")       return <HowToServe {...sharedProps} />;
  if (stepName === "skillsNeeded")     return <SkillsNeeded {...sharedProps} />;
  if (stepName === "basicInfo")        return <BasicInfo {...sharedProps} userRole={userRole} onImageUpload={handleImageUpload} imagePreview={imagePreview} uploadingImage={uploadingImage} />;
  if (stepName === "churchAffiliation") return <ChurchAffiliation {...sharedProps} />;
  if (stepName === "reference")        return <Reference {...sharedProps} submitting={submitting} />;
  if (stepName === "complete")         return (
    <OnboardingComplete
      formData={formData}
      userRole={userRole}
      userId={userId}
      onGoToExplore={() => navigate("/explore")}
      onViewProfile={() => navigate(`/profile/${userId}`)}
    />
  );

  return null;
}
